import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

// 60 Degree Angle
export class Angle60Construction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    // Layout: vertex A at left-center, baseline goes right to B, arm goes up-right to C
    const startX = options.x ?? 220;
    const startY = options.y ?? 480;
    const size = options.size ?? 280;
    const r = 110;

    const pA = { x: startX, y: startY };
    const pB = { x: startX + size, y: startY };
    // C is 60° ABOVE the baseline — in SVG, negative y = upward
    const pC = SVGRenderer.polarToCartesian(pA, size, -Math.PI / 3);

    // Arc1: centered at A, radius r, sweeps CCW from 0 (right) to -PI/3 (upper-right)
    // CCW in SVG means sweep-flag=0, going from 0 up to -60°
    // Point where arc1 hits the baseline
    const pP1 = SVGRenderer.polarToCartesian(pA, r, 0);   // { x: startX+r, y: startY }
    // Point where arc1 ends (on the arm toward C)
    const pArc1End = SVGRenderer.polarToCartesian(pA, r, -Math.PI / 3);

    // Arc2: centered at pP1, same radius r, intersects arc1 at pArc1End
    // From pP1, angle to pA is PI (pointing left), angle to pArc1End is ~-PI*2/3
    // Sweep CCW from PI to 2PI/3 (both in upper half)
    const angleP1toC = Math.atan2(pArc1End.y - pP1.y, pArc1End.x - pP1.x);
    const arc1 = new Arc(this.renderer, pA, r, 0, -Math.PI / 3, true, false);
    const arc2 = new Arc(this.renderer, pP1, r, Math.PI, angleP1toC, true, true);

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');

    const baseSegment = new Segment(this.renderer, pA, pB, false);
    const armSegment = new Segment(this.renderer, pA, pC, false);

    const angleMark = new Angle(this.renderer, pA, pB, pC, '60°');

    [pointA, pointB, pointC, baseSegment, armSegment, arc1, arc2, angleMark].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Base Ray AB',
        description: 'Draw baseline segment AB.',
        narration: 'Step 1: Drawing baseline AB.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(baseSegment, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Draw Primary Arc from A',
        description: 'Draw arc centered at vertex A with radius r, sweeping above baseline.',
        narration: 'Step 2: Drawing primary arc from vertex A.',
        duration: 1500,
        animations: [new MoveCompass(this.compass, pA, r), new DrawArc(arc1, this.compass)],
      },
      {
        id: 'step3',
        title: '3. Intersect Arc to locate 60° Point C',
        description: 'With same radius, draw arc centered at baseline intersection to find point C.',
        narration: 'Step 3: Intersecting arc to fix 60 degree point C.',
        duration: 1500,
        animations: [
          new MoveCompass(this.compass, pP1, r),
          new DrawArc(arc2, this.compass),
          new DrawPoint(pointC),
        ],
      },
      {
        id: 'step4',
        title: '4. Draw 60° Angle Arm AC',
        description: 'Connect vertex A through point C using ruler.',
        narration: 'Construction complete! 60 degree angle CAB constructed.',
        duration: 1500,
        animations: [new Fade(this.compass, false), new DrawLine(armSegment, this.ruler), new DrawPoint(angleMark as any)],
      },
    ];
  }
}

// 45 Degree Angle (Bisect 90°)
export class Angle45Construction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 220;
    const startY = options.y ?? 460;
    const size = options.size ?? 250;

    const pA = { x: startX, y: startY };
    const pB = { x: startX + size, y: startY };
    const pC = SVGRenderer.polarToCartesian(pA, size, -Math.PI / 4);

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointC = new Point(this.renderer, pC.x, pC.y, 'C');

    const baseSegment = new Segment(this.renderer, pA, pB, false);
    const arm45 = new Segment(this.renderer, pA, pC, false);
    const angleMark = new Angle(this.renderer, pA, pB, pC, '45°');

    [pointA, pointB, pointC, baseSegment, arm45, angleMark].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Draw Base Line AB',
        description: 'Draw baseline segment AB.',
        narration: 'Step 1: Drawing baseline AB.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(baseSegment, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Bisect 90° Perpendicular Angle',
        description: 'Bisect 90° vertical line using compass arcs to locate 45° angle line.',
        narration: 'Step 2: Bisecting 90 degree angle to locate 45 degree arm AC.',
        duration: 2000,
        animations: [new MoveCompass(this.compass, pA, 100), new DrawPoint(pointC)],
      },
      {
        id: 'step3',
        title: '3. Draw 45° Arm AC',
        description: 'Connect vertex A through point C using ruler.',
        narration: 'Construction complete! 45 degree angle CAB constructed.',
        duration: 1500,
        animations: [new Fade(this.compass, false), new DrawLine(arm45, this.ruler), new DrawPoint(angleMark as any)],
      },
    ];
  }
}
